import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AcademyHeader from '../../components/AcademyHeader';
import AcademyFooter from '../../components/AcademyFooter';
import ProgramIcon from '../../components/academy/ProgramIcon';
import SeoHead from '../../components/SeoHead';
import { PROGRAMS as STATIC_PROGRAMS, PROGRAM_BY_SLUG } from '../../data/programsContent';
import { ArrowRight, Clock, Users2, X } from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

/* Merge the rich static catalog with admin-created custom programs from API.
 * Static programs keep their full content; custom programs get the lightweight
 * data the admin entered. */
function useMergedPrograms() {
  const [list, setList] = useState(STATIC_PROGRAMS);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/academy/programs`).then(({ data }) => {
      if (!active) return;
      const merged = (data.programs || []).map((p) => {
        const rich = PROGRAM_BY_SLUG[p.slug];
        if (rich) return rich;
        return {
          slug: p.slug,
          title: p.name,
          tagline: p.tagline || p.description || '',
          icon: 'BookOpen',
          gradient: 'from-stone-600 to-stone-800',
          image: p.image_url || null,
          duration: p.duration || 'Self-paced',
          level: p.level || 'All levels',
          audience: p.audience || 'Aspiring learners',
          summary: p.description || '',
          isCustom: true,
        };
      });
      setList(merged);
    }).catch(() => {/* keep static fallback */});
    return () => { active = false; };
  }, []);
  return list;
}

export default function ProgramsIndex() {
  const programs = useMergedPrograms();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return programs;
    return programs.filter((p) => {
      const hay = [p.title, p.tagline, p.summary, p.slug, p.audience, p.level]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [programs, q]);

  const clearQ = () => {
    const next = new URLSearchParams(params);
    next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Academy Programs · ZHELTA"
        description="Explore all 11 ZHELTA Academy programs — certifications, programming, cybersecurity, design and more."
        canonical="/academy/programs"
      />
      <AcademyHeader />

      {/* Hero */}
      <section className="bg-white border-b border-[#E5E5E7]">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-16 lg:py-20">
          <div className="text-[11px] tracking-[0.26em] uppercase font-bold text-[#c9a227] mb-3">Programs</div>
          <h1 className="text-[38px] sm:text-[48px] lg:text-[56px] font-extrabold leading-[1.05] mb-3 text-zinc-900">Career-ready IT training<br />for every stage.</h1>
          <p className="text-[15px] lg:text-[17px] text-zinc-600 max-w-[640px]">
            11 programs spanning certification prep, programming, cybersecurity, creative tech and academic support — taught by mentors, paced for working people.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-14">
        {q && (
          <div
            className="mb-6 flex items-center gap-3 flex-wrap"
            data-testid="programs-search-summary"
          >
            <span className="text-[13px] text-zinc-600">
              {filtered.length} result{filtered.length === 1 ? '' : 's'} for{' '}
              <strong className="text-zinc-900">“{q}”</strong>
            </span>
            <button
              type="button"
              onClick={clearQ}
              data-testid="programs-search-clear"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        )}
        {filtered.length === 0 ? (
          <div
            className="text-center py-20 text-zinc-500"
            data-testid="programs-search-empty"
          >
            <p className="text-[15px] mb-3">
              No programs match <strong>“{q}”</strong>.
            </p>
            <button
              type="button"
              onClick={clearQ}
              className="text-[13px] font-semibold text-[#c9a227] hover:underline"
            >
              Show all programs
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
            <Link
              key={p.slug}
              to={`/academy/programs/${p.slug}`}
              data-testid={`program-card-${p.slug}`}
              className="group block rounded-2xl border border-[#E5E5E7] bg-white hover:border-[#c9a227] hover:shadow-[0_22px_40px_-25px_rgba(201,162,39,0.5)] transition-all overflow-hidden"
            >
              <div className={`relative aspect-[16/9] bg-gradient-to-br ${p.gradient} overflow-hidden`}>
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute left-4 bottom-4 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <ProgramIcon name={p.icon} className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
              <div className="p-5">
                <h2 className="text-[17px] font-extrabold text-[#1D1D1F] mb-1 group-hover:text-[#c9a227] transition-colors">{p.title}</h2>
                <p className="text-[13.5px] text-[#6E6E73] leading-snug mb-3 line-clamp-3">{p.summary}</p>
                <div className="flex items-center gap-4 text-[11.5px] text-[#6E6E73]">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#c9a227]" /> {p.duration}</span>
                  <span className="inline-flex items-center gap-1"><Users2 className="w-3.5 h-3.5 text-[#c9a227]" /> {p.level}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-[#c9a227]">
                  View program <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </Link>
            ))}
          </div>
        )}
      </section>

      <AcademyFooter />
    </div>
  );
}
